import { useState } from 'react';
import type { VoidFunctionComponent } from 'react';

import { window } from '../../../../globals';
import { useRevision } from '../../../../hooks/api';
import { SidebarLayout } from '../../../layouts';
import type { ReportExclusionOptions, ReportGenerationParams } from '../shared/types';

import ActionButtons from './ActionButtons';
import useReportContainer from './context/useReportContainer';
import Preview from './Preview';
import ReportFilters from './ReportFilters';
import {
  defaultFixPlanOptions,
  defaultIncludeOptions,
  ntiaflatIncludeValues,
  ntiaLicenseValues,
  ntiaMetadataCheckboxValues,
} from './shared/constants';
import Sidebar from './Sidebar';
import type { DependencyMetadataFilterOption, FormatName, IncludeFilterOption, IncludeFixPlanFilterOption } from './shared/types';

import styles from './ReportGenerationV2.module.less';

type Props = {
  params: ReportGenerationParams;
};

const ReportGenerationV2: VoidFunctionComponent<Props> = (props: Props) => {
  const { dependencyInfoOptions } = useReportContainer();
  const [format, setFormat] = useState<FormatName | null>(null);
  const [includeOptions, setIncludeOptions] = useState<IncludeFilterOption[]>([
    ...defaultIncludeOptions,
    // Also enable the NTIA toggle by default
    ...ntiaflatIncludeValues,
    ...ntiaLicenseValues,
  ]);
  const [dependencyMetadataOptions, setDependencyMetadataOptions] = useState<DependencyMetadataFilterOption[]>([
    ...dependencyInfoOptions,
    // Also enable the NTIA toggle by default
    ...ntiaMetadataCheckboxValues,
  ]);
  const [excludeOptions, setExcludeOptions] = useState<ReportExclusionOptions>({});
  const [fixPlanOptions, setFixPlanOptions] = useState<IncludeFixPlanFilterOption[]>([
    ...defaultFixPlanOptions,
  ]);
  // Add state for SBOM author and supplier information
  const [authorName, setAuthorName] = useState<string>('');
  const [authorEmail, setAuthorEmail] = useState<string>(window.user?.email || '');
  const [supplier, setSupplier] = useState<string>(window.user?.organization?.title || '');

  const [isDirty, setIsDirty] = useState(false);
  const resetFormat = () => {
    setIsDirty(false);
    setFormat(null);
  };
  const isVulnerability = props.params.report_type === 'vulnerability';

  const isProjectReport = !!props.params.project_id && !!props.params.revision_id;
  const isLicensingReport = props.params.report_type === 'licensing';
  const projectRevisionLocator = `${props.params.project_id}$${props.params.revision_id}`;

  // Fetch the current revision to determine if project reports should be downloaded or emailed.
  const revisionResponse = useRevision(projectRevisionLocator, isProjectReport && isLicensingReport);

  return (
    <div className={styles.reportGenerationV2Container}>
      <SidebarLayout>
        <SidebarLayout.Main>
          <div className={styles.reportGenerationV2}>
            <Sidebar params={props.params} resetFormat={resetFormat} />
            <Preview
              params={props.params}
              format={format}
              includeOptions={includeOptions}
              dependencyMetadataOptions={dependencyMetadataOptions}
              excludeOptions={excludeOptions}
              fixPlanOptions={fixPlanOptions}
              authorName={authorName}
              authorEmail={authorEmail}
              supplier={supplier}
            />
          </div>
        </SidebarLayout.Main>
        {!isVulnerability && (
          <SidebarLayout.Sidebar>
            <div className={styles.filterSidebar}>
              <ActionButtons
                isDisabled={isDirty}
                reportType={props.params.report_type}
                format={format}
                includeOptions={includeOptions}
                dependencyMetadataOptions={dependencyMetadataOptions}
                fixPlanOptions={fixPlanOptions}
                revision={revisionResponse.data}
                authorName={authorName}
                authorEmail={authorEmail}
                supplier={supplier}
                excludeOptions={excludeOptions}
              />
              <ReportFilters
                isApplyButtonDisabled={!isDirty}
                setIsDirty={setIsDirty}
                includeOptions={includeOptions}
                dependencyMetadataOptions={dependencyMetadataOptions}
                excludeOptions={excludeOptions}
                fixPlanOptions={fixPlanOptions}
                reportType={props.params.report_type}
                format={format}
                setFormat={setFormat}
                setIncludeOptions={setIncludeOptions}
                setDependencyMetadataOptions={setDependencyMetadataOptions}
                setFixPlanOptions={setFixPlanOptions}
                authorName={authorName}
                authorEmail={authorEmail}
                supplier={supplier}
                setAuthorName={setAuthorName}
                setAuthorEmail={setAuthorEmail}
                setSupplier={setSupplier}
                setExcludeOptions={setExcludeOptions}
              />
            </div>
          </SidebarLayout.Sidebar>
        )}
      </SidebarLayout>
    </div>
  );
};

export default ReportGenerationV2;
